// src/pages/BlogPage.js
import React, { useContext, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const BlogPage = () => {
  const { loading, getPublicPosts } = useContext(BlogContext);
  const publicPosts = getPublicPosts();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = publicPosts;

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = publicPosts.filter(post => 
        post.title.toLowerCase().includes(search) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(search)) ||
        post.author.toLowerCase().includes(search) ||
        (post.content && post.content.some(section => 
          section.value && section.value.toLowerCase().includes(search)
        ))
      );
    }

    // Sort posts
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.publishedDate || b.createdAt) - new Date(a.publishedDate || a.createdAt);
        case 'oldest':
          return new Date(a.publishedDate || a.createdAt) - new Date(b.publishedDate || b.createdAt);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'author':
          return a.author.localeCompare(b.author);
        default:
          return 0;
      }
    });

    return sorted;
  }, [publicPosts, searchTerm, sortBy]);

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <SEO
        title="Blog - Koby Pierce"
        description="Read Koby Pierce's blog posts about AI, technology, entrepreneurship, and insights from his companies. Stay updated with the latest in tech innovation."
        keywords="Koby Pierce Blog, AI Blog, Technology Blog, Entrepreneurship, Tech Innovation, Hybrid Hosting, UpPacket"
        url="https://kobypierce.com/blog"
      />
      <Header />

      {/* Hero Section */}
      <div className="w-full relative pt-6 pb-16">
        {/* Background with gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/30 via-purple-900/20 to-[#0d0d0d]"></div>
        {/* Top gradient overlay */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0d0d0d] to-transparent"></div>
        <div className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">Blog</h1>
          <p className="text-xl text-gray-300">
            Insights on AI, technology, entrepreneurship, and the future of innovation
          </p>
        </div>
        </div>
      </div>

      {/* Main container */}
      <div className="w-full bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto px-4 py-12">
          
          {/* Search and Sort Controls */}
          <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search blog posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-3">
              <label htmlFor="sort" className="text-sm text-gray-300 whitespace-nowrap">
                Sort by:
              </label>

              {/* Custom select wrapper */}
              <div className="relative">
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none px-4 py-3 pr-10 bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title (A-Z)</option>
                  <option value="author">Author (A-Z)</option>
                </select>
                {/* Custom arrow icon */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Results Count */}
          {searchTerm && (
            <div className="mb-6 text-sm text-gray-400">
              {filteredAndSortedPosts.length === 0 ? (
                `No posts found for "${searchTerm}"`
              ) : (
                `Found ${filteredAndSortedPosts.length} post${filteredAndSortedPosts.length === 1 ? '' : 's'} for "${searchTerm}"`
              )}
            </div>
          )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-300">Loading blog posts...</p>
          </div>
        ) : filteredAndSortedPosts.length === 0 ? (
          <div className="text-center py-12">
            {searchTerm ? (
              <div>
                <p className="text-xl text-gray-300 mb-4">No posts found matching "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Clear search to see all posts
                </button>
              </div>
            ) : (
              <p className="text-xl text-gray-300">No blog posts available yet.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredAndSortedPosts.map(post => (
              <Link key={post.id} to={`/blog/${post.id}`} className="group">
                <article className="bg-[#1a1a1a] rounded-lg overflow-hidden border border-[#4d4d4d] hover:border-blue-500 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl h-full flex flex-col">
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden bg-gray-800">
                    {post.headerImage ? (
                      <img
                        src={`/vendor/${post.headerImage}`}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900/30 to-purple-900/30">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-2 bg-blue-600 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-400">No Image</p>
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h2 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors">
                      {post.title}
                    </h2>
                    
                    {post.excerpt && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Flexible spacer */}
                    <div className="flex-grow"></div>

                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mt-4">
                      <div className="flex items-center space-x-2">
                        <span>{post.author}</span>
                        <span>•</span>
                        <span>{post.timeToRead}</span>
                      </div>
                      {post.publishedDate && (
                        <span>
                          {new Date(post.publishedDate + "T00:00:00").toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
        </div>
      </div>
      
      {/* Footer */}
      <div className="max-w-6xl mx-auto">
        <Footer />
      </div>
    </div>
  );
};

export default BlogPage;